import { useState } from "react";
import { toast } from "sonner";
import { Check, Send } from "lucide-react";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";

const QUESTIONS = [
  {
    section: "School operations & model (URGENT — please answer first)",
    items: [
      {
        id: "operating_model",
        q: "How is ZCA operating this first year? Choose one: (A) 100% IN-PERSON Christian day school — all students attend classes at the Alta IBC fellowship hall daily. (B) HYBRID — most students attend in-person, but a few families teach at home under ZCA's church school covering. (C) IN-PERSON school, but still filed under Alabama §16-28-1 church school statute (physical school, same legal category).",
      },
      {
        id: "school_hours_days",
        q: "What are the school hours and days? For example: Monday–Friday 8:00 AM to 3:00 PM? Any half-days, chapel days, or unique schedule for year one?",
      },
      {
        id: "grades_offered_year1",
        q: "Which grades are you actually offering in-person for the 2026–2027 year? (K–12 across the board, or a smaller range for year one — e.g., K–6 only?) Any expected class-size limits?",
      },
      {
        id: "teachers_year1",
        q: "Who are the teachers for year one? Is Brady the primary teacher, or are there additional teachers/aides? Any teacher bios or names to feature on the website?",
      },
      {
        id: "homeschool_families",
        q: "If HYBRID (option B above): about how many homeschool-covering families do you expect this first year? Do they follow the same tuition ($1,500/child) or a different rate?",
      },
      {
        id: "meals_transport",
        q: "Any details on meals (bring lunch / school-provided), transportation, or before/after care that should be mentioned on the website?",
      },
    ],
  },
  {
    section: "Approval & tone",
    items: [
      {
        id: "overall_feel",
        q: "Does the homepage feel warm, trustworthy, and unmistakably Christian? Anything feel off?",
      },
      {
        id: "hero_copy",
        q: "The hero headline reads: 'A Christian school built on Scripture, scholarship, and shepherding.' Do you love it, or would you prefer different wording?",
      },
      {
        id: "welcome_paragraph",
        q: "The welcome paragraph you provided is prominent on the hero. Should any wording be added, removed, or adjusted?",
      },
    ],
  },
  {
    section: "Pricing & payment",
    items: [
      {
        id: "tuition_correct",
        q: "$1,500 per child, per school year — is this correct? Any per-family or sibling discount to add?",
      },
      {
        id: "partner_ministry_wording",
        q: "The pricing section says 'A partner ministry is assisting with tuition costs for this first school year.' Is this how you want it worded, or would you prefer to name the ministry?",
      },
      {
        id: "payment_instructions",
        q: "Payment instructions currently say: 'Please make checks payable to Zion Christian Academy. Mail to 20670 Jimmy Lunceford Rd, Northport, AL 35475. For cash, call (205) 200-6257 to arrange drop-off.' Correct?",
      },
      {
        id: "additional_fees",
        q: "Any additional fees to publish now (transcript, diploma, report card, verification letter)? Or leave them 'contact school for pricing' until you decide?",
      },
    ],
  },
  {
    section: "Enrollment flow",
    items: [
      {
        id: "enrollment_fields",
        q: "The enrollment form collects: parent name/email/phone/address + password; per student: legal name, DOB, grade, gender; then agreement + signature + payment choice. Any fields to add or remove?",
      },
      {
        id: "additional_students",
        q: "Parents can add unlimited students in one enrollment. Should we cap this or leave unlimited?",
      },
    ],
  },
  {
    section: "Documents (Step 2 of build)",
    items: [
      {
        id: "letterhead",
        q: "The letterhead abbreviation you gave is 'ZCA' with your address, phone, and email. Sig title 'Director', signed by Brady Renfroe. Confirm — or should Pastor Eric Milligan co-sign?",
      },
      {
        id: "welcome_letter_extras",
        q: "The Welcome Letter will include your provided welcome wording plus 'next steps'. Anything specific you'd like included (a Bible verse, dress code snippet, meeting time at the fellowship hall, etc.)?",
      },
      {
        id: "boe_instructions",
        q: "The BOE Instructions letter will explain how to withdraw from public school and notify their county BOE. Should we include Alabama's specific statute reference (§16-28-1) in the letter? Any Alabama-specific language you want us to include or avoid?",
      },
    ],
  },
  {
    section: "Church & operations",
    items: [
      {
        id: "church_meeting_info",
        q: "You noted school activities will be at Alta IBC's fellowship hall (131 Willie Williams Rd, Berry AL) for the first year. Should this be public on the homepage, or kept internal until families enroll?",
      },
      {
        id: "dress_code",
        q: "You mentioned a specific dress code (purple/grey/gold shirts, khaki/denim pants; skirts for girls; closed-toe shoes). Should we publish this on the site or keep in the Welcome Letter only?",
      },
      {
        id: "admins",
        q: "Admin 1: Matthew Brady Renfroe (Director, bradyrenfroe2@icloud.com) — full access, notify all. Admin 2: Shannon Rose Renfroe (admin, shannonrenfroe608@icloud.com) — full access, notify all. Confirm both should be seeded with default passwords?",
      },
    ],
  },
  {
    section: "Optional / next phase",
    items: [
      {
        id: "logo_ok",
        q: "The logo currently used shows a shepherd's staff, open Bible with cross, and small sheep in purple + gold. Do you approve, or would you like any revisions?",
      },
      {
        id: "features_to_toggle_on",
        q: "The parent portal has toggles for: re-enrollment (on by default), transcripts (off — you set the price), student management (on). Any changes to these defaults?",
      },
    ],
  },
];

export default function QuestionnairePage() {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [answers, setAnswers] = useState({});
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!name || !email) {
      toast.error("Please add your name and email at the top.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/questionnaire", {
        submitter_name: name,
        submitter_email: email,
        answers,
      });
      setSent(true);
      if (data?.email_sent) {
        toast.success("Questionnaire saved and emailed — thank you!");
      } else {
        toast.success(
          "Questionnaire saved (email delivery had an issue, but your answers are stored safely)."
        );
      }
    } catch {
      toast.error("Failed to save. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SiteHeader />

      <section className="bg-purple-dark text-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
          <div className="text-xs tracking-[0.22em] uppercase text-gold-light font-bold mb-3">
            Internal · Client review
          </div>
          <h1 className="h-serif text-4xl sm:text-5xl tracking-tight mb-4">
            A quick questionnaire, Brady.
          </h1>
          <p className="text-white/85 leading-relaxed max-w-xl">
            Before we build the next phase (documents, portal, admin
            dashboard), please answer these questions so we get every detail
            right. It takes ~10 minutes.
          </p>
        </div>
      </section>

      <section className="max-w-3xl mx-auto px-5 sm:px-8 py-16">
        <div className="mb-8 flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={() => {
              const text = QUESTIONS.map((sec, si) => {
                const lines = [
                  `SECTION ${si + 1}: ${sec.section.toUpperCase()}`,
                  "-".repeat(60),
                ];
                sec.items.forEach((it, i) => {
                  lines.push(`${i + 1}. ${it.q}`);
                  lines.push(`   ANSWER: `);
                  lines.push("");
                });
                return lines.join("\n");
              }).join("\n\n");
              const header = `ZION CHRISTIAN ACADEMY — CLIENT REVIEW QUESTIONNAIRE\n${"=".repeat(60)}\nName: \nEmail: \nDate: \n${"=".repeat(60)}\n\n`;
              navigator.clipboard.writeText(header + text);
              toast.success("Questionnaire copied to clipboard!");
            }}
            className="inline-flex items-center gap-2 bg-purple text-white hover:bg-purple-dark font-bold px-6 py-3 rounded-sm text-sm transition-colors"
            data-testid="q-copy-btn"
          >
            Copy full questionnaire (plain text)
          </button>
          <span className="text-xs text-ink/60">
            Paste into email, Google Docs, or a text message.
          </span>
        </div>

        {sent && (
          <div className="border border-emerald-600 bg-emerald-50 text-emerald-900 p-6 mb-10 flex items-start gap-3">
            <Check size={20} className="mt-0.5" />
            <div>
              <div className="font-bold mb-1">Thank you!</div>
              <div className="text-sm">
                Your answers have been saved. We'll incorporate them into the
                next build step.
              </div>
            </div>
          </div>
        )}

        <div className="grid gap-5 sm:grid-cols-2 mb-10 border border-grey p-5 bg-grey-light/50">
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">
              Your name
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="border-grey focus:border-purple bg-white"
              data-testid="q-name"
            />
          </div>
          <div>
            <Label className="text-xs tracking-[0.15em] uppercase text-purple-dark font-bold mb-2 block">
              Your email
            </Label>
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="border-grey focus:border-purple bg-white"
              data-testid="q-email"
            />
          </div>
        </div>

        {QUESTIONS.map((sec, si) => (
          <div key={si} className="mb-12" data-testid={`q-section-${si}`}>
            <div className="text-xs tracking-[0.22em] uppercase text-gold font-bold mb-2">
              Section {si + 1}
            </div>
            <h2 className="h-serif text-purple-dark text-2xl sm:text-3xl mb-6 tracking-tight">
              {sec.section}
            </h2>
            <div className="space-y-6">
              {sec.items.map((it) => (
                <div key={it.id}>
                  <Label className="text-sm text-ink block mb-2 leading-relaxed">
                    {it.q}
                  </Label>
                  <Textarea
                    value={answers[it.id] || ""}
                    onChange={(e) =>
                      setAnswers((a) => ({ ...a, [it.id]: e.target.value }))
                    }
                    rows={3}
                    className="border-grey focus:border-purple bg-white"
                    placeholder="Your answer..."
                    data-testid={`q-${it.id}`}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}

        <button
          type="button"
          onClick={submit}
          disabled={submitting || sent}
          className="inline-flex items-center gap-2 bg-gold text-ink hover:bg-gold-light font-bold px-8 py-4 rounded-sm transition-colors disabled:opacity-60"
          data-testid="q-submit"
        >
          {sent ? "Saved" : submitting ? "Saving..." : "Submit answers"}
          {!sent && <Send size={16} />}
        </button>
      </section>

      <SiteFooter />
    </div>
  );
}
